// packages/frontend/src/services/user-fetch-service.ts

import { User } from '@msb/shared';
import { useLogger } from '../utils/logger';
import { getFunctions, httpsCallable, HttpsCallable } from 'firebase/functions';

/**
 * Service for fetching user data from Firestore via Cloud Functions
 * This service handles the async nature of user creation via onUserCreate trigger
 */
export class UserFetchService {
  private logger = useLogger('UserFetchService');
  private getUserFunction: HttpsCallable<{ userId: string }, { user: User }>;
  private completeOnboardingFunction: HttpsCallable<void, { onboardingPending: false }>;

  constructor() {
    const functions = getFunctions();
    if (import.meta.env.DEV) {
      // Connect to local emulator in development
      this.getUserFunction = httpsCallable(functions, 'getUser-getUser');
    } else {
      this.getUserFunction = httpsCallable(functions, 'getUser-getUser');
    }
    this.completeOnboardingFunction = httpsCallable(functions, 'completeOnboarding-completeOnboarding');
  }

  /**
   * Fetch user data from Firestore with retry mechanism
   * This handles the async delay between Firebase Auth user creation and Firestore document creation
   *
   * Right after signup the document does not exist yet — it is created by the
   * onUserCreate auth trigger, which also creates an organization and can
   * cold-start. Retries use exponential backoff so the window outlasts that
   * without hammering the callable.
   *
   * @param firebaseUid - Firebase Auth UID
   * @param maxRetries - Maximum number of attempts (default: 5)
   * @param retryDelayMs - Base backoff delay; doubles each attempt (default: 500ms)
   */
  async fetchUserWithRetry(
    firebaseUid: string,
    maxRetries: number = 5,
    retryDelayMs: number = 500
  ): Promise<User | null> {
    this.logger.info('Fetching user from Firestore', {
      firebaseUid,
      maxRetries,
      retryDelayMs,
    });

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Call getUser Cloud Function using the Firebase SDK
        const result = await this.getUserFunction({ userId: firebaseUid });
        
        // The backend wraps response with createSuccessResponse, so structure is:
        // { success: true, data: { user: {...} }, metadata: {...} }
        // Firebase callable returns result.data, so we get the wrapped response
        const response = result.data as any;
        
        // Log response shape only - never the actual profile data (PII/PHI).
        this.logger.info('getUser raw response', {
          resultDataType: typeof result.data,
          resultDataKeys: result.data ? Object.keys(result.data) : [],
          hasResponse: !!response,
          responseKeys: response ? Object.keys(response) : [],
        });

        // Try multiple paths to find the user object
        const user = response?.data?.user || response?.user || result.data?.user;

        this.logger.info('getUser extracted user', {
          hasUser: !!user,
          userKeys: user ? Object.keys(user) : [],
          hasProfile: !!user?.profile,
          profileKeys: user?.profile ? Object.keys(user.profile) : [],
          hasPrefix: !!user?.profile?.prefix,
        });

      if (user) {
        this.logger.info('User fetched successfully from Firestore', {
          userId: user.id,
          attempt,
          hasPrefix: !!user.profile?.prefix,
        });

        // Transform backend user structure (with nested profile) to frontend User type
        // Backend returns: { id, email, profile: { firstName, lastName, displayName, prefix }, ... }
        // Frontend expects: { id, email, firstName, lastName, prefix, ... }
        const transformedUser: User = {
          id: user.id,
          email: (user as any).email || undefined,
          phoneNumber: (user as any).phoneNumber || undefined,
          prefix: (user as any).profile?.prefix || (user as any).prefix || undefined,
          firstName: (user as any).profile?.firstName || (user as any).firstName || 'User',
          lastName: (user as any).profile?.lastName || (user as any).lastName || undefined,
          userType: (user as any).role === 'child' ? 'child' : 'therapist',
          createdDate: new Date((user as any).createdAt || Date.now()),
          lastLogin: (user as any).updatedAt ? new Date((user as any).updatedAt) : undefined,
          isActive: (user as any).isActive !== false,
          onboardingPending: (user as any).onboardingPending === true,
        };

        this.logger.debug('Transformed user', {
          hasPrefix: !!transformedUser.prefix,
        });

        return transformedUser;
      }
      } catch (error: any) {
        // The SDK throws an error for non-OK responses
        const errorCode = error.code || error.message?.code;
        const errorMessage = error.message || error.details || 'Unknown error';
        
        if (errorCode === 'not-found' || errorCode === 'functions/not-found') {
          this.logger.debug('User not found in Firestore, will retry', {
            firebaseUid,
            attempt,
            maxRetries,
            errorCode,
            errorMessage,
            fullError: JSON.stringify(error, null, 2),
          });
        } else if (errorCode === 'permission-denied' || errorCode === 'functions/permission-denied') {
          this.logger.warn('Permission denied when fetching user', {
            code: errorCode,
            message: errorMessage,
            firebaseUid,
            attempt,
            fullError: JSON.stringify(error, null, 2),
          });
          // Don't retry on permission errors - they won't succeed
          break;
        } else {
          this.logger.warn('Failed to fetch user from Firestore', {
            code: errorCode,
            message: errorMessage,
            firebaseUid,
            attempt,
            fullError: JSON.stringify(error, null, 2),
            errorKeys: Object.keys(error),
          });
        }
      }

      // Wait before retrying (except on last attempt), backing off exponentially
      // so the total window outlasts a cold-started onUserCreate trigger.
      if (attempt < maxRetries) {
        await this.delay(retryDelayMs * Math.pow(2, attempt - 1));
      }
    }

    this.logger.warn('Failed to fetch user after all retry attempts', {
      firebaseUid,
      maxRetries,
    });

    return null;
  }

  /**
   * Delay helper for retry mechanism
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clears the first-run flag once the onboarding wizard is finished. The
   * users rules do not allow clients to write it, so the server does it.
   */
  async markOnboardingComplete(): Promise<void> {
    await this.completeOnboardingFunction();
  }

  /**
   * Check if user exists in Firestore (without retry)
   */
  async checkUserExists(firebaseUid: string): Promise<boolean> {
    try {
      const result = await this.getUserFunction({ userId: firebaseUid });
      return !!result.data?.user;
    } catch (error: any) {
      if (error.code !== 'not-found') {
        this.logger.error('Failed to check user existence', {
          code: error.code,
          message: error.message,
          firebaseUid,
        });
      }
      return false;
    }
  }
}

// Export singleton instance
export const userFetchService = new UserFetchService();
